/**
 * Lightweight, framework-free validators for listing forms.
 *
 * Returns a flat `{ field_or_custom_key: errorMessage }` object.
 * An empty object means the form is valid.
 *
 * Designed to be called from both Create and Edit listing flows.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// MG numbers commonly have +261 / 0 prefix + 9 digits, but we accept any
// 8-15 digits with optional + at start (intl-friendly) for forward-compat.
const PHONE_RE = /^\+?\d{8,15}$/;

const TITLE_MIN = 5;
const TITLE_MAX = 100;
const DESC_MIN = 10;
const DESC_MAX = 2000;

const isEmpty = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

/**
 * Validate the platform-level required fields shared by every listing.
 * @param {object} input
 * @param {function} t  - i18next translator
 * @returns {object}    - errors keyed by field name
 */
export function validateBaseFields(input, t) {
  const errors = {};
  const { title, description, price, category, region, city, images } = input;

  if (isEmpty(title)) {
    errors.title = t('validation.required');
  } else if (title.trim().length < TITLE_MIN) {
    errors.title = t('validation.minLength', { n: TITLE_MIN });
  } else if (title.length > TITLE_MAX) {
    errors.title = t('validation.maxLength', { n: TITLE_MAX });
  }

  if (isEmpty(description)) {
    errors.description = t('validation.required');
  } else if (description.trim().length < DESC_MIN) {
    errors.description = t('validation.minLength', { n: DESC_MIN });
  } else if (description.length > DESC_MAX) {
    errors.description = t('validation.maxLength', { n: DESC_MAX });
  }

  // price arrives either as number or formatted string from NumberInput
  const priceNum =
    typeof price === 'number'
      ? price
      : parseInt(String(price ?? '').replace(/\D/g, ''), 10);
  if (!priceNum || priceNum <= 0) {
    errors.price = t('validation.priceRequired');
  }

  if (isEmpty(category)) errors.category = t('validation.required');
  if (isEmpty(region)) errors.region = t('validation.required');
  if (isEmpty(city)) errors.city = t('validation.required');

  if (!Array.isArray(images) || images.length === 0) {
    errors.images = t('validation.imageRequired');
  }

  return errors;
}

/**
 * Validate admin-defined custom fields against their FieldDefinition schema.
 * @param {Array}  fieldDefs    - admin field definitions
 * @param {object} customFields - raw form values keyed by field.key
 * @param {function} t          - i18next translator
 * @returns {object}            - errors keyed by field.key
 */
export function validateCustomFields(fieldDefs, customFields, t) {
  const errors = {};
  if (!Array.isArray(fieldDefs)) return errors;

  for (const def of fieldDefs) {
    const value = customFields ? customFields[def.key] : undefined;
    const v = def.validation || {};

    if (def.required && isEmpty(value)) {
      errors[def.key] = t('validation.required');
      continue;
    }
    // Optional + empty → skip remaining checks
    if (isEmpty(value)) continue;

    if (def.type === 'number') {
      const n = Number(value);
      if (Number.isNaN(n)) {
        errors[def.key] = t('validation.mustBeNumber');
        continue;
      }
      if (v.min != null && n < v.min) {
        errors[def.key] = t('validation.min', { n: v.min });
        continue;
      }
      if (v.max != null && n > v.max) {
        errors[def.key] = t('validation.max', { n: v.max });
        continue;
      }
    }

    if (def.type === 'dropdown') {
      const opts = def.options || [];
      if (opts.length > 0 && !opts.includes(String(value))) {
        errors[def.key] = t('validation.invalidOption');
        continue;
      }
    }

    if (def.type === 'multi_select') {
      const arr = Array.isArray(value) ? value : [value];
      const opts = def.options || [];
      const bad = arr.find((x) => !opts.includes(x));
      if (bad) {
        errors[def.key] = t('validation.invalidOption');
        continue;
      }
    }

    if (def.type === 'date') {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) {
        errors[def.key] = t('validation.invalidDate');
        continue;
      }
    }

    if (def.type === 'text') {
      const s = String(value);
      if (v.min_length != null && s.length < v.min_length) {
        errors[def.key] = t('validation.minLength', { n: v.min_length });
        continue;
      }
      if (v.max_length != null && s.length > v.max_length) {
        errors[def.key] = t('validation.maxLength', { n: v.max_length });
        continue;
      }
      if (v.pattern === 'email' && !EMAIL_RE.test(s.trim())) {
        errors[def.key] = t('validation.invalidEmail');
        continue;
      }
      if (v.pattern === 'phone' && !PHONE_RE.test(s.replace(/[\s.-]/g, ''))) {
        errors[def.key] = t('validation.invalidPhone');
        continue;
      }
    }
  }

  return errors;
}

/** Combine base + custom field errors into one object. */
export function validateListing(input, fieldDefs, t) {
  return {
    ...validateBaseFields(input, t),
    ...validateCustomFields(fieldDefs, input.customFields, t),
  };
}
